import React from "react";
import { 
  User, 
  Lock, 
  Check, 
  Copy, 
  ExternalLink, 
  Download, 
  Info, 
  Ellipsis, 
  ArrowRight, 
  ShieldCheck,
  Globe,
  Menu
} from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PostsProvider } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <PostsProvider wp_query={wp_query}>
      <div data-section-id="5955" id="single-post-template-section" className="min-h-screen bg-background font-sans text-foreground">
        {}
        <header className="bg-primary text-primary-foreground py-4 px-6 shadow-md sticky top-0 z-50">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <Link to="/HomePage" className="flex items-center">
              <img 
                src="/img/british_council_logo_white.png" 
                alt="EuroIndiaTrade" 
                className="h-8 md:h-10 w-auto object-contain" 
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="#" className="flex items-center gap-2 text-primary-foreground hover:text-primary-foreground/80 transition-colors">
                <User className="w-5 h-5" />
                <span className="text-base font-medium hidden sm:inline">Partner Login</span>
              </Link>
            </div>
          </div>
        </header>

        {}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {}
            <div className="lg:col-span-2 space-y-8">
              
              {}
              <Card className="bg-card border-none shadow-md rounded-lg overflow-hidden">
                <CardContent className="p-6 md:p-10">
                  {}
                  <div className="flex flex-col sm:flex-row justify-between items-start mb-8 gap-4">
                    <img 
                      src="/img/british_council_logo_blue.png" 
                      alt="EuroIndiaTrade" 
                      className="h-8 md:h-10 w-auto object-contain" 
                    />
                    <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                      <img 
                        src="/img/qr_code_aptis.png" 
                        alt="Verification QR" 
                        className="h-12 w-12 object-contain" 
                      />
                      <div className="flex items-center gap-1.5 bg-muted text-muted-foreground px-3 py-1.5 rounded-md border border-border">
                        <Lock className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium uppercase tracking-wide">Verified</span>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="mb-8">
                    <h1 className="text-2xl font-bold flex flex-wrap gap-2">
                      <span className="text-chart-3">Market</span>
                      <span className="text-secondary">Readiness Profile</span>
                    </h1>
                  </div>

                  {}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6 mb-10">
                    <div className="border-b border-border pb-1">
                      <p className="text-xs text-muted-foreground mb-1">Company name</p>
                      <p className="text-base font-bold text-primary">Global Logistics Ltd</p>
                    </div>
                    <div className="border-b border-border pb-1">
                      <p className="text-xs text-muted-foreground mb-1">Audit date</p>
                      <p className="text-base font-bold text-primary">22.05.2025</p>
                    </div>
                    <div className="border-b border-border pb-1">
                      <p className="text-xs text-muted-foreground mb-1">Entity ID</p>
                      <p className="text-base font-bold text-primary">EIT~0478495</p>
                    </div>
                    <div className="border-b border-border pb-1">
                      <p className="text-xs text-muted-foreground mb-1">Region</p>
                      <p className="text-base font-bold text-primary">EU-India Corridor</p>
                    </div>
                    <div className="border-b border-border pb-1 md:col-span-2">
                      <p className="text-xs text-muted-foreground mb-1">Service package</p>
                      <p className="text-base font-bold text-primary">Premium Trade Membership</p>
                    </div>
                  </div>

                  {}
                  <div className="flex flex-col md:flex-row gap-8">
                    {}
                    <div className="w-full md:w-1/2">
                      <h3 className="text-base font-bold text-secondary mb-4">Readiness metrics</h3>
                      <div className="space-y-0">
                        {[
                          { label: "Compliance", score: "48/50" },
                          { label: "Logistics", score: "42/50" },
                          { label: "Market Access", score: "40/50" },
                          { label: "Partnership", score: "45/50" },
                          { label: "Aggregate Index", score: "175/200", bold: true },
                          { label: "ESG & Ethics", score: "44/50" },
                        ].map((item, i) => (
                          <div 
                            key={i} 
                            data-index={i}
                            className={`flex justify-between py-2 border-b border-border ${item.bold ? "font-bold bg-muted px-2 -mx-2" : ""}`}
                          >
                            <span className="text-sm text-primary">{item.label}</span>
                            <span className="text-sm text-primary">{item.score}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {}
                    <div className="w-full md:w-1/2">
                      <h3 className="text-base font-bold text-secondary mb-4">Maturity profile</h3>
                      <div className="flex gap-4 items-end h-48 bg-secondary/10 p-4 rounded-lg relative mb-2">
                        {}
                        <div className="absolute left-2 top-2 bottom-2 flex flex-col justify-between text-[10px] text-muted-foreground h-full py-1">
                          <span>Top</span>
                          <span>High</span>
                          <span>Mid</span>
                          <span>Reg</span>
                          <span>Low</span>
                          <span>New</span>
                        </div>
                        
                        {}
                        <div className="flex-1 flex items-end justify-around pl-6 h-full pb-1">
                          {[
                            { label: "C", height: "90%", grade: "High" },
                            { label: "L", height: "80%", grade: "High" },
                            { label: "M", height: "70%", grade: "Mid" },
                            { label: "P", height: "85%", grade: "High" },
                            { label: "O", height: "82%", grade: "High", color: "bg-primary" }
                          ].map((bar, i) => (
                            <div key={i} data-index={i} className="flex flex-col items-center gap-1 w-6 h-full justify-end">
                              <span className="text-[10px] font-bold text-primary">{bar.grade}</span>
                              <div 
                                className={`w-full rounded-t-sm ${bar.color || "bg-chart-3"}`} 
                                style={{ height: bar.height }}
                              ></div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground text-center">
                        See secondary page for regulatory compliance descriptors.
                      </p>
                    </div>
                  </div>

                  {}
                  <div className="mt-8 pt-6 border-t border-border flex flex-col gap-2">
                    <Link to="/MarketEntryPage" className="text-sm text-chart-3 hover:underline w-fit">
                      www.euroindiatrade.com/market-entry
                    </Link>
                    <Link to="#" className="text-xs text-muted-foreground hover:underline break-all">
                      https://trust.euroindiatrade.com/verify/a227fdc0-478f-4b7b-9e6a-aa3101fc74c4
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {}
              <div className="space-y-6 px-2 md:px-0">
                <div className="flex items-center gap-2">
                  <img src="/img/british_council_logo_blue.png" alt="EIT" className="h-6 w-auto" />
                  <span className="text-base font-bold text-primary">EuroIndiaTrade Authority</span>
                  <ExternalLink className="w-4 h-4 text-primary" />
                </div>

                <h2 className="text-xl font-bold text-primary">Expanding Trade Horizons</h2>

                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" className="bg-card border-border text-primary hover:bg-muted rounded-md">
                    <Download className="w-4 h-4 mr-2" /> EXPORT REPORT
                  </Button>
                  <Button variant="outline" className="bg-card border-border text-primary hover:bg-muted rounded-md">
                    <Info className="w-4 h-4 mr-2" /> GUIDANCE
                  </Button>
                  <Button variant="outline" className="bg-card border-border text-primary hover:bg-muted rounded-md">
                    <Ellipsis className="w-4 h-4 mr-2" /> ACTIONS
                  </Button>
                </div>

                <Link to="#" className="inline-block text-base text-primary underline hover:no-underline">
                  Join the network for advanced trade analytics
                </Link>

                <div className="flex items-center gap-4 py-4 border-t border-b border-border">
                  <Avatar className="h-12 w-12 bg-primary text-primary-foreground">
                    <AvatarImage src="/img/rajesh_kumar_avatar.png" alt="Rajesh Kumar" />
                    <AvatarFallback className="bg-primary text-primary-foreground font-bold">RK</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-base font-bold text-primary">Rajesh Kumar</p>
                    <Link to="/TradeServicesPage" className="flex items-center text-base text-primary underline hover:no-underline mt-1">
                      View Verified Certificates <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>

                <div className="space-y-4 text-base text-primary leading-relaxed">
                  <p>
                    EuroIndiaTrade.com is a leading B2B digital ecosystem connecting the vibrant markets of Europe and India. Our platform facilitates seamless discovery of verified suppliers, logistics experts, and regulatory consultants to streamline international operations.
                  </p>
                  <p>
                    We leverage advanced data mapping and verification systems to ensure that every partner on our network meets the high standards required for cross-border trade. Our readiness reports provide a comprehensive overview of a company's capability to navigate the EU-India Free Trade Agreement framework.
                  </p>
                  <p>
                    By offering precise metrics on trade compliance and market maturity, we empower businesses to make informed decisions regarding recruitment, investment, and supply chain optimization across both territories.
                  </p>
                  <p>
                    Trusted by over 8500 organizations including manufacturing giants and SME exporters, EuroIndiaTrade.com is the definitive bridge for economic cooperation between the European Union and the Indian subcontinent.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-8 pt-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">REGISTERED ON</p>
                    <p className="text-base text-primary">23 May 2025</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">STATUS</p>
                    <p className="text-base text-primary">Active Member</p>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="lg:col-span-1 space-y-6">
              
              {}
              <Card className="bg-card border-none shadow-md rounded-lg">
                <CardContent className="p-6 space-y-6">
                  <h3 className="text-base font-bold text-primary">Trust Verification</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 bg-secondary rounded-full p-0.5 shrink-0">
                        <Check className="w-3 h-3 text-secondary-foreground" />
                      </div>
                      <p className="text-sm text-primary">
                        This entity is a <span className="font-bold">Verified Trade Partner</span>
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Lock className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
                      <p className="text-sm text-primary">Blockchain Secured ID</p>
                    </div>
                  </div>

                  <Button variant="outline" className="w-full justify-center bg-card border-border text-primary hover:bg-muted rounded-md">
                    <Copy className="w-4 h-4 mr-2" /> Copy Partner ID
                  </Button>

                  <Button className="w-full justify-center bg-primary text-primary-foreground hover:opacity-90 shadow-none rounded-md">
                    Verify Credentials
                  </Button>
                </CardContent>
              </Card>

              {}
              <Card className="bg-card border-none shadow-md rounded-lg">
                <CardContent className="p-6 space-y-6">
                  <h3 className="text-base font-bold text-primary">Platform Authority</h3>
                  
                  <div className="flex items-center gap-3">
                    <img src="/img/british_council_logo_blue.png" alt="EIT" className="h-8 w-auto" />
                    <span className="text-base font-bold text-primary">EuroIndiaTrade</span>
                  </div>

                  <div className="space-y-3">
                    <Button variant="outline" className="w-full justify-between bg-card border-border text-primary hover:bg-muted rounded-md">
                      Official Portal <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" className="w-full justify-between bg-card border-border text-primary hover:bg-muted rounded-md">
                      Trade Regulatory Hub <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>

                  <Link to="/RegulatoryPage" className="flex items-center text-base text-primary underline hover:no-underline">
                    Regulatory Compliance Data <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>

            </div>
          </div>
        </main>

        {}
        <footer className="bg-primary text-primary-foreground py-8 px-6 mt-12">
          <div className="max-w-7xl mx-auto space-y-2">
            <p className="text-sm">© 2024 EuroIndiaTrade.com</p>
            <p className="text-sm">The digital bridge for strategic European and Indian business partnerships.</p>
            <p className="text-sm">Supporting the EU–India Free Trade Agreement and economic cooperation.</p>
          </div>
        </footer>
      </div>
    </PostsProvider>
  );
}